import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSeoMeta } from "@/lib/useSeoMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyScanBar from "@/components/StickyScanBar";
import ScanBox from "@/components/ScanBox";
import { ReferencesMarquee } from "@/components/SocialProof";
import { STRIPE_LINK_SCAN_20 } from "@/lib/stripeLinks";
import RisksCarousel from "@/components/RisksCarousel";
import { Button } from "@/components/ui/button";
import { getPage, pageUrl, alternatesFor, pagePath } from "@/content/seo";
import { orgAndWebSite, softwareApplication } from "@/lib/schemaOrg";
import { ArrowRight, ShieldCheck, FileCheck2, BellRing, GitBranch, Globe2, Search, Scale, Sparkles, Check, Gavel, Lock, ScanLine, ImageIcon, FileText } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&w=1400";
const LEAF_IMG = "https://images.unsplash.com/photo-1527698500164-35f561ab70ae?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function Landing({ lang: routeLang = "fr" }) {
  const { t, lang, setLang } = useLang();
  useEffect(() => { if (routeLang !== lang) setLang(routeLang); }, [routeLang, lang, setLang]);

  const page = getPage("home", routeLang);
  const canonical = pageUrl("home", routeLang);
  useSeoMeta({
    title: page.title,
    description: page.description,
    keywords: page.keywords,
    canonical,
    alternates: alternatesFor("home"),
    jsonLd: [
      orgAndWebSite(routeLang),
      softwareApplication(routeLang, canonical),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage: routeLang,
        mainEntity: page.faq.map(([q, a]) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
    ],
  });
  const nav = useNavigate();

  return (
    <div className="App">
      <Header variant="public" />
      <StickyScanBar />

      {/* HERO */}
      <section className="relative overflow-hidden brand-mesh">
        <div className="grid-paper absolute inset-0" />
        <div className="grain absolute inset-0" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 pt-10 sm:pt-16 md:pt-24 pb-14 sm:pb-20 md:pb-28 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="fade-up inline-flex items-center gap-2 border border-primary/20 bg-white/70 backdrop-blur px-3 py-1.5 rounded-full mb-6 sm:mb-8">
              <Gavel className="w-3.5 h-3.5 text-primary" />
              <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-primary">{t("hero.eyebrow")}</span>
            </div>
            <h1 className="fade-up delay-1 text-[30px] sm:text-[38px] md:text-[48px] lg:text-[54px] leading-[1.05] tracking-tight text-foreground" data-testid="hero-title">
              {page.h1}
            </h1>
            <p className="fade-up delay-1 mt-4 font-serif text-xl sm:text-2xl text-primary italic">
              {t("brand.baseline.l1")} {t("brand.baseline.l2")}
            </p>
            <p className="fade-up delay-2 mt-5 sm:mt-6 text-base sm:text-lg max-w-xl text-muted-foreground leading-relaxed">{t("hero.sub")}</p>

            <div className="fade-up delay-3 mt-8 sm:mt-10 max-w-2xl">
              <ScanBox testidPrefix="hero" />
            </div>
            <div className="fade-up delay-4 mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
              {[t("hero.trust1"), t("hero.trust2"), t("hero.trust3")].map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-secondary" /> {s}</span>
              ))}
            </div>
          </div>

          {/* Right: visual */}
          <div className="lg:col-span-5 relative fade-up delay-2 md:mt-6">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(31,90,153,0.35)]">
              <img src={HERO_IMG} alt="Audit des allégations environnementales sur un site e-commerce — directive UE 2024/825" className="w-full h-[280px] sm:h-[360px] md:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-1">{t("hero.card.eyebrow")}</p>
                <p className="font-serif text-2xl leading-tight">cosmeticos.fr</p>
                <div className="mt-4 flex gap-3">
                  <div className="bg-white/15 backdrop-blur rounded-lg px-3 py-2 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-white/70">{t("report.red")}</p>
                    <p className="text-2xl font-serif text-[#FCA5A5]">4</p>
                  </div>
                  <div className="bg-white/15 backdrop-blur rounded-lg px-3 py-2 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-white/70">{t("report.orange")}</p>
                    <p className="text-2xl font-serif text-[#FCD34D]">7</p>
                  </div>
                  <div className="bg-white/15 backdrop-blur rounded-lg px-3 py-2 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-white/70">{t("hero.card.pages")}</p>
                    <p className="text-2xl font-serif">15</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative -mt-8 md:-ml-8 hidden md:block hairline p-4 max-w-[300px] card-lift bg-white z-10">
              <div className="flex gap-3 items-start">
                <span className="tag-red px-1.5 h-5 text-[10px] font-mono flex items-center rounded">RED</span>
                <p className="text-sm font-serif italic leading-snug">« Notre crème 100% naturelle, respectueuse de la planète. »</p>
              </div>
              <div className="mt-3 pt-3 border-t border-border flex gap-3 items-start">
                <span className="tag-green px-1.5 h-5 text-[10px] font-mono flex items-center rounded">FIX</span>
                <p className="text-sm font-serif italic leading-snug text-[hsl(142_60%_32%)]">« 92% d'ingrédients d'origine naturelle certifiés COSMOS. »</p>
              </div>
            </div>
            <div className="absolute -right-4 -top-6 hidden md:flex hairline px-4 py-3 items-center gap-3 card-lift">
              <BellRing className="w-4 h-4 text-[hsl(var(--status-orange))]" />
              <div>
                <p className="text-xs font-semibold">{t("hero.alert.title")}</p>
                <p className="text-[10px] font-mono text-muted-foreground">J-30 · 12.03.26</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KPI band */}
      <section className="border-y border-border bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-6 sm:py-8 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {[
            ["27.09.2026", t("kpi.1")],
            ["≤ 24 h", t("kpi.2")],
            ["6", t("kpi.3")],
            ["100 %", t("kpi.4")],
          ].map(([v, l]) => (
            <div key={l} className="border-l-2 border-secondary pl-4">
              <p className="font-serif text-3xl text-primary">{v}</p>
              <p className="text-xs text-muted-foreground mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REFERENCES MARQUEE — juste après hero KPIs, sous la barre visuelle */}
      {/* Bandeau "Ils ont testé EmpCo" retiré 2026-09-30 :
          risque juridique (identification IP ≠ référence client, usage marques sans accord). */}

      {/* RISKS CAROUSEL — ce que vous risquez vraiment (juste avant Méthode) */}
      <section id="risks" className="bg-background border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-16">
          <RisksCarousel />
        </div>
      </section>

      {/* VIDEO — uniquement page FR, juste sous "Ce que vous risquez vraiment" */}
      {routeLang === "fr" && (
        <section id="video" className="bg-background border-b border-border">
          <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-border bg-black aspect-video">
              <video
                data-testid="landing-video-fr"
                className="w-full h-full"
                controls
                playsInline
                preload="metadata"
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_allegation-registry/artifacts/8wg6yhgz_EmpCo_motion_16x9.mp4"
              >
                Votre navigateur ne prend pas en charge la lecture vidéo.
              </video>
            </div>
          </div>
        </section>
      )}

      {/* METHODE */}
      <section id="method" className="bg-background">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-14 sm:py-20 md:py-24">
          <div className="grid lg:grid-cols-12 gap-6 sm:gap-10 mb-10 sm:mb-14 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-3">02 · {t("nav.method")}</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight">{t("features.h")}</h2>
            </div>
            <p className="lg:col-span-5 text-muted-foreground leading-relaxed">{t("features.intro")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 sm:gap-5">
            <Step n="01" Icon={Globe2} title={t("step.1t")} desc={t("step.1d")} className="md:col-span-2" />
            <Step n="02" Icon={Search} title={t("step.2t")} desc={t("step.2d")} className="md:col-span-2" />
            <Step n="03" Icon={Sparkles} title={t("step.3t")} desc={t("step.3d")} className="md:col-span-2" accent />
            <div className="md:col-span-2 md:row-span-1 rounded-xl overflow-hidden relative min-h-[240px]">
              <img src={LEAF_IMG} alt="Allégation écologique et preuve environnementale : registre des allégations EmpCo" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F5A99]/85 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <Scale className="w-5 h-5 mb-3" />
                <p className="font-serif text-lg leading-snug">{t("features.quote")}</p>
              </div>
            </div>
            <Step n="04" Icon={GitBranch} title={t("step.4t")} desc={t("step.4d")} className="md:col-span-2" />
            <Step n="05" Icon={ShieldCheck} title={t("step.5t")} desc={t("step.5d")} className="md:col-span-1" compact />
            <Step n="06" Icon={FileCheck2} title={t("step.6t")} desc={t("step.6d")} className="md:col-span-1" compact />
          </div>
        </div>
      </section>

      {/* 3 MODES DE SCAN */}
      <section id="modes" className="bg-white border-t border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-14 sm:py-20 md:py-24">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <p className="eyebrow mb-3 inline-flex items-center gap-2"><ScanLine className="w-3.5 h-3.5" /> {t("modes.eyebrow")}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight mb-4">{t("modes.h")}</h2>
            <p className="text-muted-foreground leading-relaxed">{t("modes.sub")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {[{ Icon: Globe2, k: "site" }, { Icon: ImageIcon, k: "image" }, { Icon: FileText, k: "document" }].map(({ Icon, k }) => (
              <div key={k} className="hairline p-7 rounded-xl card-lift bg-white" data-testid={`mode-card-${k}`}>
                <div className="w-12 h-12 rounded-lg bg-accent text-primary flex items-center justify-center mb-5"><Icon className="w-5 h-5" strokeWidth={1.8} /></div>
                <h3 className="font-serif text-xl mb-2">{t(`modes.${k}.t`)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(`modes.${k}.d`)}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] font-mono text-muted-foreground mt-8">{t("modes.foot")}</p>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-white border-y border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-14 sm:py-20 md:py-24">
          <div className="mb-10 sm:mb-14 max-w-2xl">
            <p className="eyebrow mb-3">03 · {t("nav.pricing")}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight">{t("pricing.h")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 items-stretch">
            <PricingCard testid="pricing-free" n="01" title={t("pricing.free")} price={t("pricing.free.price")} desc={t("pricing.free.d")} cta={t("pricing.cta.free")} onClick={() => document.querySelector('[data-testid="hero-scan-input"]')?.scrollIntoView({behavior:"smooth"})}/>
            <PricingCard testid="pricing-paid" n="02" title={t("pricing.paid")} price={t("pricing.paid.price")} oldPrice={t("pricing.paid.old")} desc={t("pricing.paid.d")} cta={t("pricing.cta.paid")} highlight badge={t("pricing.popular")} discount="-50%" onClick={() => { window.location.href = STRIPE_LINK_SCAN_20; }}/>
            <PricingCard testid="pricing-sub" n="03" title={t("pricing.sub")} price={t("pricing.sub.price")} oldPrice={t("pricing.sub.old")} desc={t("pricing.sub.d")} cta={t("pricing.cta.sub")} discount="-50%" onClick={() => nav("/register?next=sub")}/>
            <PricingCard testid="pricing-enterprise" n="04" title={t("pricing.enterprise")} price={t("pricing.enterprise.price")} desc={t("pricing.enterprise.d")} cta={t("pricing.cta.enterprise")} onClick={() => nav(lang === "fr" ? "/contact" : `/${lang}/contact`)}/>
          </div>
        </div>
      </section>

      {/* LEGAL band */}
      <section id="legal" className="bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full border-[28px] border-white/5" />
        <div className="absolute right-40 -top-32 w-[300px] h-[300px] rounded-full border-[20px] border-white/5" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-14 sm:py-20 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-white/60 mb-3">04 · {t("nav.legal")}</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl tracking-tight text-white">{t("legal.h")}</h2>
          </div>
          <div className="text-white/80 leading-relaxed text-sm space-y-5">
            <p>{t("legal.p1")}</p>
            <p className="font-mono text-[11px] text-white/60 border-l-2 border-secondary pl-3">{t("legal.p2")}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-white text-primary px-5 py-3 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors" data-testid="legal-cta">
                {t("legal.cta")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={pagePath("country-guide", routeLang)} className="inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-white/30 px-5 py-3 rounded-lg text-sm font-semibold hover:bg-white hover:text-primary transition-colors" data-testid="legal-directive-cta">
                {t("legal.directive_cta")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Step({ n, Icon, title, desc, className = "", accent = false, compact = false }) {
  return (
    <div className={`hairline card-lift p-6 flex flex-col ${accent ? "bg-accent border-primary/20" : ""} ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent ? "bg-primary text-white" : "bg-accent text-primary"}`}>
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <span className="font-mono text-xs text-muted-foreground">{n}</span>
      </div>
      <h3 className={`text-lg mb-2 leading-snug ${compact ? "text-base" : ""}`}>{title}</h3>
      {!compact && <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>}
      {compact && <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>}
    </div>
  );
}

function PricingCard({ testid, n, title, price, oldPrice, desc, cta, onClick, highlight = false, badge, discount }) {
  return (
    <div className={`relative p-8 rounded-xl flex flex-col card-lift ${highlight ? "bg-primary text-white shadow-[0_25px_50px_-20px_rgba(31,90,153,0.5)] md:-my-4 md:py-12" : "hairline"}`} data-testid={testid}>
      {/* Badge -50% flottant en haut à droite */}
      {discount && (
        <span className="absolute -top-3 -right-3 z-10 bg-rose-600 text-white w-14 h-14 rounded-full flex flex-col items-center justify-center text-[10px] font-bold leading-tight shadow-lg shadow-rose-500/40" data-testid={`${testid}-discount`}>
          <span className="text-sm">{discount}</span>
          <span className="text-[9px] uppercase">Réduc</span>
        </span>
      )}
      <div className="flex items-baseline justify-between mb-6">
        <p className={`font-mono text-[11px] uppercase tracking-widest ${highlight ? "text-white/60" : "text-muted-foreground"}`}>{n}</p>
        {badge && <span className={`px-2.5 py-0.5 text-[10px] font-semibold uppercase rounded-full ${highlight ? "bg-secondary text-white" : "bg-secondary text-white"}`}>{badge}</span>}
      </div>
      <h3 className={`text-2xl mb-3 leading-tight ${highlight ? "text-white" : ""}`}>{title}</h3>
      {oldPrice && (
        <p className={`text-lg font-serif line-through mb-1 ${highlight ? "text-white/50" : "text-slate-400"}`} data-testid={`${testid}-old-price`}>{oldPrice}</p>
      )}
      <p className={`text-4xl tracking-tight mb-5 font-serif ${highlight ? "text-white" : "text-primary"}`}>{price}</p>
      <ul className={`text-sm mb-8 space-y-2 ${highlight ? "text-white/80" : "text-muted-foreground"}`}>
        {desc.split("·").map((d) => (
          <li key={d} className="flex gap-2"><Check className={`w-4 h-4 shrink-0 mt-0.5 ${highlight ? "text-[#6CC070]" : "text-secondary"}`} />{d.trim()}</li>
        ))}
      </ul>
      <Button onClick={onClick} className={`mt-auto w-full h-12 rounded-lg font-semibold ${highlight ? "bg-white text-primary hover:bg-white/90" : "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))]"}`} data-testid={`${testid}-cta`}>
        {cta} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      {!highlight && <Lock className="w-3 h-3 absolute top-8 right-8 text-transparent" />}
    </div>
  );
}
